// O REPASSE AOS CREDORES, NO PORTAL DO ORGAO (#279).
//
// Pagina propria, e nao uma guia de Troca de arquivos: quem so' responde pelo
// repasse (`responsavel_repasse`) nao alcanca a troca de arquivos, e mesmo
// assim e' quem registra. Le quem atende a unidade em qualquer papel; registra
// quem processa a folha ou responde pelo repasse — e a pessoa precisa da
// permissao `repasses`. A API confere os dois; a tela so' esconde o botao.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, TextField } from "@atlas/ui/web";
import { papelLibera, type Papel } from "@atlas/types";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import { podeAcessar, readPrefeituraPermissoesFromJwt } from "../../lib/prefeitura-perms";
import { RepassesPainel } from "../../components/RepassesPainel";

/** AAAAMM do mes atual, no relogio de quem abre a tela. */
function competenciaAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function permissoesDoLogin(): string[] | null {
  try {
    const raw = window.localStorage.getItem("atlas:tokens");
    return readPrefeituraPermissoesFromJwt(raw ? JSON.parse(raw)?.access_token : null);
  } catch {
    return null;
  }
}

export function PrefeituraRepasses() {
  const { unidade, opcoesDoSdk, chaveDaUnidade } = useUnidadeAtiva();
  const [competencia, setCompetencia] = useState(competenciaAtual());
  const valida = /^\d{4}(0[1-9]|1[0-2])$/.test(competencia);

  // Os credores vem na mesma resposta da lista: a quem a unidade repassa.
  const credoresQ = useQuery({
    queryKey: ["prefeitura", "repasses", competencia, ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.repasses.listar(competencia, opcoesDoSdk()),
    enabled: valida,
  });
  const credores = useMemo(
    () => (credoresQ.data?.credores ?? []).map((c) => ({ value: String(c.credorId), label: c.nome })),
    [credoresQ.data],
  );

  const naOrigem = unidade?.origem !== false;
  const papeis = (unidade?.papeis ?? []) as Papel[];
  const podeRegistrar =
    podeAcessar(permissoesDoLogin(), "repasses") && (naOrigem || papelLibera(papeis, "repasses", "escrita"));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <header>
        <h1 style={{ margin: 0 }}>Repasses aos credores</h1>
        <p style={{ margin: "4px 0 0" }}>
          O que o órgão transferiu a cada consignatária do que a folha descontou. É a terceira coluna da conciliação:
          a consignatária confere pedido, descontado e repassado.
        </p>
      </header>
      <Card>
        <TextField
          label="Competência"
          placeholder="AAAAMM"
          value={competencia}
          onChange={(e) => setCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6))}
          error={valida ? undefined : "AAAAMM"}
        />
      </Card>
      {valida ? (
        <RepassesPainel
          titulo={`Repasses de ${competencia.slice(4)}/${competencia.slice(0, 4)}`}
          credores={credores}
          podeRegistrar={podeRegistrar}
          porta={{
            chave: ["prefeitura", "repasses", competencia, ...chaveDaUnidade()],
            listar: () => atlas.prefeitura.repasses.listar(competencia, opcoesDoSdk()),
            registrar: (p) => atlas.prefeitura.repasses.registrar(competencia, p, opcoesDoSdk()),
            comprovanteUrl: (id) => atlas.prefeitura.repasses.comprovanteUrl(competencia, id, opcoesDoSdk()),
          }}
        />
      ) : null}
    </div>
  );
}
